import React, { useMemo, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { Link as RouterLink } from 'react-router-dom';
import UpgradeNudge from '../../common/UpgradeNudge';
import {
    DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext, rectSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { AuthService } from '../../../services/auth.service';
import { isWidgetVisible } from '../registry';
import { homeFallbackFor, stripPresetFor } from '../registry/presets';
import { isGatedModule } from '../registry/moduleMeta';
import { useDashboardAccess } from '../useDashboardAccess';
import useDashboardLayout from './useDashboardLayout';
import ActionCenter from './ActionCenter';
import BoardKpiStrip from './BoardKpiStrip';
import WidgetCard from './WidgetCard';
import AddWidgetDialog from './AddWidgetDialog';

const SPAN: Record<string, number> = { sm: 4, md: 6, lg: 12 };

function SortableWidget({ id, children }: { id: string; children: React.ReactNode }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    return (
        <Box
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            sx={{ height: '100%' }}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
        >
            {children}
        </Box>
    );
}

export default function DashboardBoard() {
    const { hasPermission } = useAuth();
    const toast = useToast();
    const canManageKpiTargets = usePermission(PERMISSIONS.DASHBOARD_KPI_TARGET_MANAGE);
    const institutionId = AuthService.getSelectedInstitution();

    // Each user sees only their own role's board (the one persisted per user+institution, V97).
    // There is deliberately no switch to preview another role's dashboard.
    const { primary, showUpgradeHint, enabledModules } = useDashboardAccess();
    // The admin wildcard (holds every board key) keeps the whole-business tiles and Action Center.
    const stripPreset = stripPresetFor(primary, hasPermission);

    // Same 'home' layout key as before, so saved layouts still load. The admin wildcard's unsaved
    // fallback stays its pre-role-dashboard list (homeFallbackFor); real roles get their own preset.
    const { order, widgets, locked, add, remove, reorder, reset } = useDashboardLayout({
        institutionId, boardKey: 'home', fallbackIds: homeFallbackFor(primary, hasPermission), editable: true,
        onError: e => toast.showApiError?.(e, 'Could not save the dashboard'),
    });
    // A widget whose module the institution has since disabled must disappear rather than hang on
    // a permanent spinner (its fetch would 403 and the widget swallows that error). `order`/`widgets`
    // (and their persisted layout) keep the hidden entry so it reappears if the module is re-enabled;
    // only rendering + the drag surface are filtered. Ungated modules are never in `enabledModules`,
    // so they must not be filtered out — see isGatedModule. A widget the user's roles no longer grant
    // is hidden the same way.
    const visibleWidgets = useMemo(
        () => widgets.filter(def =>
            (!isGatedModule(def.module) || enabledModules.has(def.module))
            && isWidgetVisible(def, hasPermission)),
        [widgets, enabledModules, hasPermission],
    );
    const [addOpen, setAddOpen] = useState(false);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const onDragEnd = (e: DragEndEvent) => {
        if (!e.over || e.active.id === e.over.id) return;
        const from = order.indexOf(String(e.active.id));
        const to = order.indexOf(String(e.over.id));
        if (from >= 0 && to >= 0) reorder(from, to);
    };

    return (
        <Box sx={{ minWidth: 0 }} data-help-page="dashboard.board">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }} data-help-section="dashboard.toolbar">
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} data-help="dashboard.addWidget">Add widget</Button>
                <Button size="small" startIcon={<RestartAltIcon />} onClick={reset} data-help="dashboard.reset">Reset to default</Button>
                {canManageKpiTargets && (
                    <Button size="small" startIcon={<TrackChangesIcon />} component={RouterLink} to="/dashboard/settings/targets" data-help="dashboard.kpiTargets">
                        KPI targets
                    </Button>
                )}
            </Stack>

            {showUpgradeHint && (
                <UpgradeNudge
                    sx={{ mb: 2 }}
                    message={
                        <>
                            Unlock the <strong>Business owner dashboard</strong> — cross-module KPIs, attendance
                            trends, and people insights — with a premium subscription. Contact your account
                            manager to enable it for this institution.
                        </>
                    }
                />
            )}

            <BoardKpiStrip institutionId={institutionId} scope="home" preset={stripPreset} />

            {/* The auditor board is read-only: nothing is waiting on them, so no Action Center. */}
            {stripPreset !== 'auditor' && <ActionCenter institutionId={institutionId} preset={stripPreset} />}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={visibleWidgets.map(w => w.id)} strategy={rectSortingStrategy}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                        {visibleWidgets.map(def => (
                            <Box key={def.id} sx={{ gridColumn: { xs: 'span 12', md: `span ${SPAN[def.size]}` } }}>
                                <SortableWidget id={def.id}>
                                    <WidgetCard def={def} institutionId={institutionId} editable locked={locked.includes(def.id)} onRemove={() => remove(def.id)} />
                                </SortableWidget>
                            </Box>
                        ))}
                    </Box>
                </SortableContext>
            </DndContext>

            <AddWidgetDialog
                open={addOpen}
                hasPermission={hasPermission}
                present={order}
                enabledModules={Array.from(enabledModules)}
                onAdd={add}
                onClose={() => setAddOpen(false)}
            />
        </Box>
    );
}
